import React, { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';
import moment from 'moment';
import { NavLink } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import {
  dateFormatter,
  infoFormatterIntl,
  infoFormatter,
  localeFormatter,
  vehiclesStatusFormatter,
  federalFawFormatter,
} from '../../../utils/formatters';
import { getRegisterData } from '../reducer';
import { loadSmevCheckVehicleList, smevCheckAllVehicles } from '../actions';
import { responseLogWithNotification } from '../../../utils/utils';
import RecipientView from './RecipientView';
import SZLTMiniGrid from './SZLTMiniGrid';
import CarrierMiniGrid from './CarrierMiniGrid';
import InsuranceView from '../../Application/components/ViewBlocks/InsuranceView';
import { APPLICATION_STATUS, REGION_REGISTRY_RELATION } from '../../../configs';
import DocumentList from '../../Application/components/DocumentList';
import { RegistryRelationHistoryDialog } from './RegistryRelationHistoryDialog';
import RegistryPageModal from './RegistryPageModal';
import ApplicationLegend from '../../Application/components/ApplicationLegend';
import AccordionWithPreview from '../../../components/base/Accordion/AccordionWithPreview';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import MobileSmevChecker from './MobileSmevChecker';

export default function MobileVehiclesView(props) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const {
    onClose = () => {}, loadData = () => {}, formMeta,
  } = props;
  const [fullscreen, setFullscreen] = useState(false);
  const [isHistoryPopUp, setIsHistoryPopUp] = useState(false);
  const [isSameRegistryPopUp, setIsSameRegistryPopUp] = useState(false);

  const {
    currentVehicles = {},
    smevCheckVehicleList = [],
    loadingCheckAllVehicles,
  } = useSelector(getRegisterData);

  useEffect(() => {
    dispatch(loadSmevCheckVehicleList(currentVehicles.id)).catch(responseLogWithNotification);
  }, []);

  const { carrier } = currentVehicles;
  const carriers = [];
  if (carrier && carrier.id) {
    carriers.push(carrier);
  }

  return ReactDOM.createPortal(
    <div className="application-form">
      <TimerLoader loading={loadingCheckAllVehicles} />

      <div className="mobile-application-form">
        <div className="mobile-header">
          <div className="application-number">
            <div className="application-text">
              Запись ЛТ №{currentVehicles.number}
            </div>
            <div className="legend-button">
              <Icons width={24} height={24} customIcon icon="helper" className="legend-icon" />
              <ApplicationLegend />
            </div>
          </div>
          <div className="application-status-block">
            {vehiclesStatusFormatter(currentVehicles.status)}
            {federalFawFormatter(currentVehicles?.federal_law_number)}
            <div className="afc-header-link-same mobile-header-link">
              <Icons
                customIcon
                icon="same-registry"
                className="history-icon"
                onClick={() => setIsSameRegistryPopUp(true)}
              />
              {isSameRegistryPopUp
                ? (
                  <RegistryPageModal
                    type="vehicle"
                    onClose={() => setIsSameRegistryPopUp(false)}
                    customFilter={{
                      vehicle_vin: currentVehicles?.vehicle_vin,
                    }}
                    title="Реестровые записи в отношении данного ЛТ"
                    filterName="vehicle-same-list-modal-filter"
                  />
                ) : null}
            </div>

            <NavLink
              className="mobile-header-link"
              to={{
                pathname: '/application',
                search: new URLSearchParams({
                  filter: 'carrier',
                  status: Object.values(APPLICATION_STATUS),
                  number: currentVehicles.number,
                  created_at: [moment(currentVehicles.created_at).subtract(1, 'year').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
                }).toString(),
              }}
              target="_blank"
            >
              <Icons
                className="history-icon"
                customIcon
                icon="show-relation-link"
              />
            </NavLink>
            <NavLink
              className="mobile-header-link"
              to={{
                pathname: '/region-registry/history',
                search: new URLSearchParams({
                  subject_id: currentVehicles.id,
                  subject_type: 'vehicle',
                }).toString(),
              }}
              target="_blank"
            >
              <Icons
                className="history-icon"
                customIcon
                icon="history-relation"
              />
            </NavLink>
          </div>
        </div>
        <div className="smev-info-block">
          <div className="smev-info-title f4">
            Перечень запросов СМЭВ/СМЭВ СПб
          </div>
          <div className="smev-info-button" onClick={() => setFullscreen(!fullscreen)}>
            <Icons
              customIcon
              icon={fullscreen ? 'full-screen-close' : 'full-screen'}
            />
          </div>
          {fullscreen ? (
            <MobileSmevChecker
              list={smevCheckVehicleList}
              current={currentVehicles}
              startCheckAction={smevCheckAllVehicles}
              loadData={loadData}
              handleClose={() => setFullscreen(false)}
            />
          ) : null}
        </div>
        <div className="mobile-application-data custom-scroll-theme">
          <Row className="row-cols-2">
            {infoFormatterIntl('registry.number', currentVehicles.number, formMeta, 'number')}
            {infoFormatterIntl('created_at', dateFormatter(currentVehicles.created_at), formMeta, 'created_at')}
          </Row>
          <Row className="row-cols-2">
            {infoFormatterIntl('effective_date', dateFormatter(currentVehicles.effective_date), formMeta, 'effective_date')}
            {infoFormatterIntl('expiration_date', dateFormatter(currentVehicles.expiration_date), formMeta, 'expiration_date')}
          </Row>
          {
            [APPLICATION_STATUS.SUSPENDED].includes(currentVehicles.status) ? (
              <Row className="row-cols-3">
                {infoFormatter('Основание', currentVehicles.rejection_reason_messages?.[0]?.name || '')}
                {infoFormatter('Дополнительное основание', currentVehicles?.rejection_reason_messages?.[0]?.additional_message || '')}
                {infoFormatter('Комментарий', currentVehicles.rejection_reason_messages?.[0]?.message || '')}
              </Row>
            ) : null
          }
          <RecipientView current={currentVehicles} isVehicle formMeta={formMeta} />
          <div className="hr-row" />
          <div className="f4">Информация о транспортном средстве</div>
          <Row className="row-cols-3">
            {infoFormatterIntl('vehicle_brand', currentVehicles.vehicle_brand, formMeta, 'vehicle_brand')}
            {infoFormatterIntl('vehicle_model', currentVehicles.vehicle_model, formMeta, 'vehicle_model')}
            {infoFormatterIntl('vehicle_year', currentVehicles.vehicle_year, formMeta, 'vehicle_year')}
          </Row>
          <Row className="row-cols-3">
            {infoFormatterIntl('vehicle_color', currentVehicles.vehicle_color, formMeta, 'vehicle_color')}
            {infoFormatterIntl('vehicle_lpn', currentVehicles.vehicle_lpn, formMeta, 'vehicle_lpn')}
            {infoFormatterIntl('vehicle_vin', currentVehicles.vehicle_vin, formMeta, 'vehicle_vin')}
          </Row>
          <Row className="row-cols-2">
            {infoFormatterIntl('vehicle_registration_number', currentVehicles.vehicle_registration_number, formMeta, 'vehicle_registration_number')}
            {infoFormatterIntl('vehicle_registration_issue_date', dateFormatter(currentVehicles.vehicle_registration_issue_date), formMeta, 'vehicle_registration_issue_date')}
          </Row>
          <Row className="row-cols-2">
            {infoFormatterIntl('has_special_lifting_device', currentVehicles.has_special_lifting_device ? 'Есть' : 'Нет', formMeta, 'has_special_lifting_device')}
            {infoFormatterIntl('ecology_class', localeFormatter('ecology-class', currentVehicles.ecology_class), formMeta, 'ecology_class')}
          </Row>
          <div className="hr-row" />
          <div className="f4">Дополнительная информация</div>
          <InsuranceView data={currentVehicles} formMeta={formMeta} />
          <Row className="row-cols-1">
            {infoFormatterIntl('technical_control_address', currentVehicles.technical_control_address, formMeta, 'technical_control_address')}
          </Row>
          <Row className="row-cols-2">
            {infoFormatterIntl('decision', currentVehicles.decision, formMeta, 'decision')}
            {infoFormatterIntl('decision_date', dateFormatter(currentVehicles.decision_date), formMeta, 'decision_date')}
          </Row>
          <Row className="row-cols-2">
            {infoFormatterIntl('ownership_type', formatMessage({ id: `ownership.type.${currentVehicles.ownership_type}` }), formMeta, 'ownership_type')}
            {infoFormatterIntl('ownership_contract_number', currentVehicles.ownership_contract_number, formMeta, 'ownership_contract_number')}
          </Row>
          <Row className="row-cols-2">
            {infoFormatterIntl('ownership_contract_effective_date', dateFormatter(currentVehicles.ownership_contract_effective_date), formMeta, 'ownership_contract_effective_date')}
            {infoFormatterIntl('ownership_contract_expiration_date', dateFormatter(currentVehicles.ownership_contract_expiration_date), formMeta, 'ownership_contract_expiration_date')}
          </Row>
          <div className="hr-row" />
          <AccordionWithPreview
            title="Документы"
            content={(
              <DocumentList
                id={currentVehicles?.id}
                loadData={loadData}
              />
            )}
          />
          <CarrierMiniGrid
            params={{
              vehicle_id: currentVehicles.id,
            }}
            setIsHistoryPopUp={setIsHistoryPopUp}
          />
          <SZLTMiniGrid
            params={{
              vehicle_id: currentVehicles.id,
            }}
            setIsHistoryPopUp={setIsHistoryPopUp}
            isCarrierRelationType={false}
          />
          {isHistoryPopUp ? (
            <RegistryRelationHistoryDialog
              currentCarrier={currentVehicles}
              subject_type="vehicle"
              subject_id={currentVehicles?.id}
              log_name={isHistoryPopUp === 'szlt' ? REGION_REGISTRY_RELATION.VEHICLE_ORDER_SERVICE : REGION_REGISTRY_RELATION.VEHICLE_CARRIER}
              onClose={() => setIsHistoryPopUp(false)}
            />
          ) : null}
        </div>
        <div className="mobile-application-footer">
          <Button variant="secondary" onClick={onClose}>Закрыть</Button>
        </div>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
